(ns frontend.view.modals.bulk-edit-entity.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.constraints :as constraints]
   [common.schema.data-types :as data-types]
   [common.schema.field-spec :as field-spec]
   [common.schema.registry :as registry]
   [common.status :as statuses]
   [common.util :as cu]
   [frontend.components.field-editor :as field-editor]
   [frontend.logging :as logging]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.modals.bulk-edit-entity.events :as events]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.modal.view :as modal]
   [ui.components.button.view :as button]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn normalize-field-attribute
  [attribute]
  (if (sequential? attribute)
    (last attribute)
    attribute))

(defn get-bulk-editable-fields
  [{:keys [entity-type target-asset-type setup-args]}]
  (let [column-keys-fn (:column-keys setup-args)
        tenant-schema @session/tenant-schema
        column-keys
        (->>
         (column-keys-fn)
         (filter
          (fn [attribute]
            (let [target-asset-attr? (sequential? attribute)
                  attribute (normalize-field-attribute attribute)

                  field-schema
                  (registry/get-field-schema tenant-schema attribute)

                  field-entity-type (if target-asset-attr?
                                      target-asset-type
                                      entity-type)

                  data-type (:field/data-type field-schema)

                  allowed-ref-type? (if (some #{data-type}
                                              [:data.type/ref-multi
                                               :data.type/ref-single])
                                      (not-any? #{:entity.type/attachment}
                                                (:field/entity-types field-schema))
                                      true)

                  constraints (-> field-schema
                                  :field/constraints)

                  unique-field? (->> constraints
                                     (mapv first)
                                     (some #{:constraint/unique-global
                                             :constraint/unique-tenant-ignore-case
                                             :constraint/unique-tenant-ignore-case-additional-attribute}))

                  selected-pipeline (case entity-type
                                      :entity.type/oppo @session/selected-oppo-pipeline
                                      :entity.type/fundraising-opportunity @session/selected-froppo-pipeline
                                      :entity.type/sales-opportunity @session/selected-sales-oppo-pipeline
                                      :entity.type/exit-opportunity @session/selected-exit-oppo-pipeline
                                      nil)

                  write-access? (f-permissions/field-write-access?
                                 attribute
                                 field-entity-type
                                 selected-pipeline)

                  bulk-editable? (-> field-schema
                                     :field/data-type
                                     (data-types/zapflow-data-types)
                                     :bulk-editable?)

                  not-shown-in-create? (-> field-schema
                                           :field/not-shown-in-create?)

                  always-mandatory? (field-spec/system-required-or-legacy-always-mandatory-field?
                                     field-schema)

                  blocked-override? (some #{attribute}
                                          [:fund-commitment/won-by
                                           :fundraising-opportunity/investor
                                           :holding/target-asset
                                           :kyc-profile/target
                                           :kyc-profile/status
                                           :oppo/pass-reason-v2
                                           :oppo/pass-reason
                                           :oppo/pass-reason-secondary
                                           :oppo/last-hold-comment
                                           :sales-opportunity/customer
                                           :sale-agreement/won-by
                                           :exit-opportunity/buyer])]

              (case entity-type
                ; Todo - this is quick workaround for transition period for new tasks view
                :entity.type/task (some #{attribute}
                                        [:task/assignee :task/assigner
                                         :task/due :task/completed?])

                (and write-access?
                     bulk-editable?
                     allowed-ref-type?
                     (not unique-field?)
                     (not always-mandatory?)
                     (not not-shown-in-create?)
                     (not blocked-override?)))))))]
    column-keys))

(defn group-by-entity-type
  [main-entity-type attribute]
  (let [target-attribute? (sequential? attribute)]
    (if target-attribute?
      (let [attribute (normalize-field-attribute attribute)
            entity-type-string (-> attribute
                                   str
                                   (string/split #"/")
                                   first
                                   (subs 1))
            entity-keyword (keyword (str "entity.type/" entity-type-string))]
        entity-keyword)
      main-entity-type)))

(defn pipeline-change-form
  [{:keys [state selected-pipeline entity-type reset-final-confirmation!]}]
  (let [new-pipeline (get-in @state [:change-pipeline :pipeline])
        new-stage (get-in @state [:change-pipeline :stage])

        new-pipeline-options (->> @session/pipelines
                                  (cu/filter-by :pipeline/entity-type entity-type)
                                  (remove (fn [pipeline]
                                            (= (:db/id pipeline) selected-pipeline)))
                                  (remove :common/disabled?)
                                  (map (fn [a]
                                         {:value (:db/id a)
                                          :label (entity/display-string a)})))

        new-pipeline-stage-options (if new-pipeline
                                     (->> @session/stages
                                          (cu/filter-by :stage/pipeline new-pipeline)
                                          (sort-by :common/ord)
                                          (map (fn [a]
                                                 {:key   (:db/id a)
                                                  :label (entity/display-string a)})))
                                     [])]
    [:<>
     [:div
      [:h4 {:class "text-xl font-bold"} "2. Select pipeline"]
      [:h4 "Select the pipeline you want to move selected deals to."]

      [data-input/view
       {:editor-type :editor.type/select
        :data-type   :data.type/ref-single
        :value       new-pipeline
        :options     new-pipeline-options
        :on-change   (fn [new-value & _]
                       (swap! state assoc-in [:change-pipeline :pipeline] new-value)
                       (reset-final-confirmation!))}]]

     (when new-pipeline
       [:div
        [:h4 {:class "font-bold text-xl"} "3. Select stage"]
        [:h4 "Select the stage you want to move deals to in the pipeline"]

        [data-input/view
         {:editor-type :editor.type/select
          :data-type   :data.type/ref-single
          :value       new-stage
          :options     new-pipeline-stage-options
          :on-change   (fn [new-value & _]
                         (swap! state assoc-in [:change-pipeline :stage] new-value)
                         (reset-final-confirmation!))}]])]))

(defn status-change-form
  [{:keys [state selected-pipeline reset-final-confirmation!]}]
  (let [tenant-schema @session/tenant-schema
        new-status (:status @state)

        status-options (->> statuses/oppo-status-options
                            (cu/remove-by :key :oppo.status/portfolio)
                            (map (fn [{:keys [key label]}]
                                   {:value key :label label})))]
    [:<>
     [:div
      [:h4 {:class "text-xl font-bold"}
       "2. Select status"]
      [:h4 "Select the status you want to set selected deals to."]]

     [data-input/view
      {:editor-type :editor.type/select
       :data-type   :data.type/keyword
       :value       new-status
       :options     status-options
       :on-change   (fn [new-value & _]
                      (swap! state assoc :status new-value)
                      (reset-final-confirmation!))}]

     (condp = new-status
       :oppo.status/on-hold
       [:div
        [:h4
         {:class "mt-3 text-xl font-bold"}
         "3. Details"]

        (let [preset-months [1 2 3 6 12]
              months (get @state :months)]
          [:div
           [labelled-input/view
            {:editor-type :editor.type/select
             :label       (loc/tr :until)
             :mandatory?  true
             :value       months
             :options     (concat (->> preset-months
                                       (map (fn [num]
                                              {:value num
                                               :label (if (= num 1)
                                                        (loc/tr :month-one)
                                                        (loc/tr :months-num [num]))})))
                                  [{:value 0
                                    :label (loc/tr :select-custom-date)}])
             :on-change   (fn [new-value & _]
                            (swap! state assoc :months new-value)
                            (reset-final-confirmation!))}]

           (when (= months 0)
             [:div
              [data-input/view
               {:data-type   :data.type/date
                :value       (get @state :until)
                :placeholder (loc/tr :select-date)
                :on-change   (fn [new-value]
                               (swap! state assoc :until new-value)
                               (reset-final-confirmation!))}]])])

        [:div
         [labelled-input/view
          {:data-type :data.type/text-simple
           :label     (loc/tr :put-on-hold-curtain/internal-comment)
           :value     (get @state :internal-comment)
           :on-change (fn [new-value]
                        (swap! state assoc :internal-comment new-value)
                        (reset-final-confirmation!))}]]

        [:div.private-info-container
         [:i.fal.fa-user-secret]
         [:div.private-info
          (loc/tr :pass-curtain/private-info)]]]

       :oppo.status/passed
       [:div
        [:h4
         {:class "mt-3 text-xl font-bold"}
         "3. Details"]

        (let [pass-reason-schema (registry/get-field-schema tenant-schema :oppo/pass-reason-v2)
              options (->> (fu/named-value-options pass-reason-schema selected-pipeline)
                           (map (fn [nv]
                                  {:value (:db/id nv)
                                   :label (entity/display-string nv)})))]
          [:div
           [data-input/view
            {:editor-type :editor.type/select
             :data-type   :data.type/ref-single
             :value       (get @state :pass-reason)
             :options     options
             :placeholder (loc/tr :pass-reason-placeholder)
             :on-change   (fn [new-value & _]
                            (swap! state assoc :pass-reason new-value)
                            (reset-final-confirmation!))}]])

        (when (f-permissions/field-write-access? :oppo/pass-reason-secondary
                                                 :entity.type/oppo
                                                 selected-pipeline)
          (let [secondary-schema (registry/get-field-schema tenant-schema :oppo/pass-reason-secondary)
                options (->> (fu/named-value-options secondary-schema selected-pipeline)
                             (map (fn [nv]
                                    {:value (:db/id nv)
                                     :label (entity/display-string nv)})))]
            [:div
             [data-input/view
              {:editor-type :editor.type/select
               :data-type   :data.type/ref-single
               :value       (get @state :pass-reason-secondary)
               :options     options
               :placeholder (loc/tr :pass-reason-secondary-placeholder)
               :on-change   (fn [new-value & _]
                              (swap! state assoc :pass-reason-secondary new-value)
                              (reset-final-confirmation!))}]]))

        (when (f-permissions/field-write-access? :oppo/cur-broken-deal-expenses
                                                 :entity.type/oppo
                                                 selected-pipeline)
          [:div
           [labelled-input/currency-view
            {:label     (loc/tr :oppo/cur-broken-deal-expenses)
             :data-type :data.type/currency-value
             :value     (get @state :broken-deal-expenses)
             :on-change (fn [new-value]
                          (swap! state assoc :broken-deal-expenses new-value)
                          (reset-final-confirmation!))
             :secondary-args
             {:data-type :data.type/currency-symbol
              :value     (or (get @state :broken-deal-currency)
                             (:tenant/default-currency @session/tenant))
              :on-change (fn [new-value & _]
                           (swap! state assoc :broken-deal-currency new-value)
                           (reset-final-confirmation!))}}]])

        [:div
         [labelled-input/view
          {:data-type :data.type/text-simple
           :label     (loc/tr :pass-curtain/internal-pass-comment)
           :value     (get @state :internal-comment)
           :on-change (fn [new-value]
                        (swap! state assoc :internal-comment new-value)
                        (reset-final-confirmation!))}]]

        [:div.private-info-container
         [:i.fal.fa-user-secret]
         [:div.private-info
          (loc/tr :pass-curtain/private-info)]]]

       nil)]))

(defn stage-change-form
  [{:keys [state selected-pipeline entity-type reset-final-confirmation!]}]
  (let [new-stage (get-in @state [:change-stage :stage])

        stage-options (->> @session/stages
                           (cu/filter-by :stage/pipeline selected-pipeline)
                           (sort-by :common/ord)
                           (map (fn [a]
                                  {:value (:db/id a)
                                   :label (entity/display-string a)})))]
    [:<>
     [:div
      [:h4 {:class "text-xl font-bold"}
       "2. Select stage"]
      [:h4 "Select the stage you want to move selected deals to."]

      [data-input/view
       {:editor-type :editor.type/select
        :data-type   :data.type/ref-single
        :value       new-stage
        :options     stage-options
        :on-change   (fn [new-value & _]
                       (swap! state assoc-in [:change-stage :stage] new-value)
                       (reset-final-confirmation!))}]]]))

(defn get-entity-label
  [entity-type count]
  (let [lookup {:entity.type/oppo                    {:single   :deal
                                                      :multiple :deals}
                :entity.type/fundraising-opportunity {:single   :fundraising-opportunity
                                                      :multiple :fundraising-opportunities}
                :entity.type/sales-opportunity       {:single   :opportunity
                                                      :multiple :opportunities}
                :entity.type/exit-opportunity        {:single   :exit-opportunity
                                                      :multiple :exit-opportunities}
                :entity.type/person                  {:single   :contact
                                                      :multiple :contacts}
                :entity.type/company                 {:single   :company
                                                      :multiple :companies}
                :entity.type/task                    {:single   :task
                                                      :multiple :tasks}
                :entity.type/investor                {:single   :investor
                                                      :multiple :investors}
                :entity.type/sale-agreement          {:single   :sale-agreement
                                                      :multiple :sale-agreements}
                :entity.type/real-estate             {:single   :real-estates
                                                      :multiple :real-estates}
                :entity.type/fof-asset               {:single   :fof-asset
                                                      :multiple :fof-assets}
                :entity.type/other-asset             {:single   :other-asset
                                                      :multiple :other-assets}
                :entity.type/holding                 {:single   :holding
                                                      :multiple :holdings}
                :entity.type/fund-commitment         {:single   :commitment
                                                      :multiple :commitments}
                :entity.type/fund                    {:single   :fund
                                                      :multiple :funds}
                :entity.type/kyc-profile             {:single   :kyc-profile
                                                      :multiple :kyc-profiles}
                :entity.type/contract-kyc-profile    {:single   :contract-kyc-profile
                                                      :multiple :contract-kyc-profiles}
                :entity.type/screening-profile       {:single   :screening-profile
                                                      :multiple :screening-profiles}
                :entity.type/company-counterparty-diligence
                {:single   :counterparty-diligence/company-diligence
                 :multiple :counterparty-diligence/company-diligences}
                :entity.type/person-counterparty-diligence
                {:single   :counterparty-diligence/person-diligence
                 :multiple :counterparty-diligence/person-diligences}
                :entity.type/project-counterparty-diligence
                {:single   :counterparty-diligence/project-diligence
                 :multiple :counterparty-diligence/project-diligences}}
        multiplicity (if (= count 1)
                       :single
                       :multiple)
        default-entity-name (if (= count 1)
                              :entity
                              :entities)

        entity-label (-> lookup
                         (get-in [entity-type multiplicity] default-entity-name)
                         (loc/tr))]
    entity-label))

(defn get-modal-title
  [entity-type count]
  (let [entity-label (get-entity-label entity-type count)]
    (str (loc/tr :bulk-edit) " - " count " " entity-label "")))

;; some fields are :editor.type/none (i.e. shouldn't be visible in the original
;;  entity-editor) e.g. tag-fields, that should still be editable here:
(defn update-editor
  [{:field/keys [default-visibility data-type] :as field-schema}]
  (let [editor-type (or (:editor/type field-schema)
                        (:editor-type field-schema)
                        (-> default-visibility
                            :editor))
        default-data-type-editor (->> data-types/zapflow-data-types
                                      data-type
                                      :default-editor)]
    (if (= editor-type :editor.type/none)
      (assoc field-schema
             :editor/type
             default-data-type-editor)
      field-schema)))

(defn view
  [{:keys [setup-args entity-type close-modal!]}]
  (let [initial-state {:selected-field      nil
                       :content-type        :field-data                                             ; Todo - editing content-type will be possible later. Fixed for now.
                       :action-type         nil
                       :action-target       nil
                       :final-confirmation? false
                       :error?              false}

        state (r/atom initial-state)

        value-selector-atom (r/atom nil)

        selected-entity-ids (grid/get-selected-items setup-args)

        selected-entities (->> selected-entity-ids
                               (mapv session/get-entity))

        selected-entities-count (count selected-entities)
        entity-label (get-entity-label entity-type selected-entities-count)
        modal-title (get-modal-title entity-type selected-entities-count)

        ; Todo - support for more entity types and their target assets
        target-asset-keyword (cond
                               (= entity-type :entity.type/investor) :investor/legal-entity
                               (= entity-type :entity.type/oppo) :oppo/target-asset
                               :else nil)

        all-selected-entities-have-target-assets?
        (->> selected-entities
             (remove (fn [entity]
                       (not (get entity target-asset-keyword))))
             count
             (= (count selected-entity-ids)))

        selected-pipeline (case entity-type
                            :entity.type/oppo
                            @session/selected-oppo-pipeline

                            :entity.type/fundraising-opportunity
                            @session/selected-froppo-pipeline

                            :entity.type/sales-opportunity
                            @session/selected-sales-oppo-pipeline

                            :entity.type/exit-opportunity
                            @session/selected-exit-oppo-pipeline

                            nil)

        pipeline-target-asset-types (some-> selected-pipeline
                                            (session/get-entity)
                                            :pipeline/target-asset-types)

        target-asset-types (->> selected-entity-ids
                                (mapv session/get-entity)
                                (remove (fn [entity]
                                          (not (get entity target-asset-keyword))))
                                (map (fn [entity]
                                       (-> entity
                                           (get target-asset-keyword)
                                           (session/get-entity)
                                           :entity/type)))
                                (into #{}))

        allow-target-asset-editing? (and all-selected-entities-have-target-assets?
                                         (= (count target-asset-types) 1))

        target-asset-type (when allow-target-asset-editing?
                            (first target-asset-types))

        tenant-schema @session/tenant-schema

        mandatory-fields (:mandatory? setup-args)

        bulk-editable-fields (get-bulk-editable-fields
                              {:entity-type       entity-type
                               :target-asset-type target-asset-type
                               :setup-args        setup-args})

        grouped-entity-fields (->> bulk-editable-fields
                                   (group-by
                                    (partial group-by-entity-type entity-type)))

        entity-attributes (get grouped-entity-fields entity-type)
        target-asset-attributes (get grouped-entity-fields target-asset-type)
        common-asset-attributes (get grouped-entity-fields :entity.type/asset)

        ; Workaround for some currency fields not showing the word 'currency' in the label by default
        get-possibly-fixed-field-label (fn [attribute]
                                         (let [attribute (normalize-field-attribute attribute)
                                               field-schema (registry/get-field-schema tenant-schema attribute)
                                               field-data-type (:field/data-type field-schema)
                                               field-label (loc/field-name attribute field-schema)]
                                           (if (and (= field-data-type :data.type/currency-symbol)
                                                    (not (string/includes? field-label "currency")))
                                             (str field-label " (currency)")
                                             field-label)))

        field-options (concat (->> entity-attributes
                                   (mapv (fn [attribute]
                                           (let [attribute (normalize-field-attribute attribute)
                                                 field-label (get-possibly-fixed-field-label attribute)]
                                             {:label (str (loc/tr entity-type) " - " field-label)
                                              :value attribute}))))

                              (when allow-target-asset-editing?
                                (->> target-asset-attributes
                                     (concat common-asset-attributes)
                                     (mapv normalize-field-attribute)
                                     (mapv (fn [attribute]
                                             (let [field-label (get-possibly-fixed-field-label attribute)]
                                               {:label (str (loc/tr target-asset-type) " - " field-label)
                                                :value attribute})))
                                     (sort-by :label))))

        reset-final-confirmation! #(swap! state dissoc :final-confirmation?)

        execute-bulk-edit! (fn [continue?]
                             (events/execute-bulk-edit!
                              {:state                       state
                               :entity-type                 entity-type
                               :selected-entity-ids         selected-entity-ids
                               :allow-target-asset-editing? allow-target-asset-editing?
                               :target-asset-type           target-asset-type
                               :target-asset-keyword        target-asset-keyword
                               :continue?                   continue?
                               :success-callback!           (fn []
                                                              (reset! value-selector-atom nil)
                                                              (reset! state initial-state)

                                                              (when-not continue?
                                                                (close-modal!))

                                                              (rf/dispatch [:open-notification
                                                                            {:title       (loc/tr :bulk-edit-success-msg)
                                                                             :type        :success
                                                                             :auto-close? true}]))
                               :error-callback!             (fn [e]
                                                              (swap! state assoc :error? true))}))]
    (fn [{:keys [close-modal!]}]
      (let [action-type (:action-type @state)
            selected-field (normalize-field-attribute (:selected-field @state))
            value (:value @state)
            field-schema (when selected-field
                           (registry/get-field-schema tenant-schema selected-field))
            field-data-type (:field/data-type field-schema)
            is-multi? (data-types/multi-data-type? field-data-type)
            mandatory? (some mandatory-fields [selected-field])
            not-clearable? (or (field-spec/system-required-field? field-schema)
                               (field-spec/not-clearable-after-set-field? field-schema)
                               mandatory?)

            final-confirmation? (:final-confirmation? @state)
            execute-btn-disabled? (not final-confirmation?)
            error? (:error? @state)

            temp-value (or (:temp-value @value-selector-atom)
                           (:temp-select-value @value-selector-atom))
            value-selector-has-focus? (:has-focus? @value-selector-atom)]

        (when (and temp-value
                   (not= value temp-value))
          (swap! state dissoc :final-confirmation?)
          (swap! state assoc :value temp-value))

        (when value-selector-has-focus?
          (swap! state dissoc :final-confirmation?))

        [modal/view
         {:visible?     true
          :hide!        close-modal!
          :backdrop      "static"
          :close-button? true}
         [:span
          modal-title]
         [:<>
          [:div
           {:class "flex flex-col gap-4"}
           [:div
            {:class "flex flex-col gap-3"}
            [:div
             [:div
              ^{:key (str "bulk-edit-" selected-field)}
              [labelled-input/view
               {:editor-type :editor.type/select
                :label       (if selected-field
                               (loc/tr :selected-field)
                               (loc/tr :select-field))
                :data-type   :data.type/keyword
                :value       selected-field
                :options     field-options
                :placeholder (str (loc/tr :select) "...")
                :on-change   (fn [new-value & _]
                               (reset! state initial-state)
                               (reset! value-selector-atom nil)
                               (when (and new-value (not= new-value (keyword "")))
                                 (swap! state assoc :selected-field (normalize-field-attribute new-value))))}]]]

            (when selected-field
              (let [action-type-options
                    (concat [{:value :replace
                              :label (loc/tr :overwrite-content)}]

                            (when is-multi?
                              [{:value :add
                                :label (loc/tr :add-content)}])

                            (when-not not-clearable?
                              (if is-multi?
                                [{:value :clear-selected
                                  :label (loc/tr :clear-selected)}
                                 {:value :clear-all
                                  :label (loc/tr :clear-all)}]
                                [{:value :clear-all
                                  :label (loc/tr :clear-all)}])))]
                [:div
                 [labelled-input/view
                  {:editor-type :editor.type/select
                   :label       (if action-type
                                  (loc/tr :selected-action)
                                  (loc/tr :select-action))
                   :data-type   :data.type/keyword
                   :value       action-type
                   :options     action-type-options
                   :placeholder (str (loc/tr :select) "...")
                   :on-change   (fn [new-value & _]
                                  (when (and new-value (not= new-value (keyword "")))
                                    (swap! state assoc :action-type new-value)
                                    (swap! state dissoc :value :error? :final-confirmation?)
                                    (reset! value-selector-atom nil)

                                    (let [selected-field (:selected-field @state)
                                          selected-field (normalize-field-attribute selected-field)
                                          field-schema (when selected-field
                                                         (registry/get-field-schema tenant-schema selected-field))
                                          field-data-type (:field/data-type field-schema)]
                                      (when (contains? #{:data.type/date :data.type/datetime} field-data-type)
                                        (let [date (js/Date.)]
                                          (.setUTCHours date 0 0 0 0)
                                          (swap! state assoc :value (.getTime date)))))))}]]))

            (when (and action-type
                       (not= action-type :clear-all))
              (let [field-schema (update-editor field-schema)
                    editor-props (potpuri/deep-merge
                                  field-schema
                                  {:external-editor-atom value-selector-atom
                                   :label                (get-possibly-fixed-field-label selected-field)
                                   :on-save!             (fn [_ value]
                                                           (if (= value #{""})
                                                             (swap! value-selector-atom dissoc
                                                                    :temp-select-value
                                                                    :temp-value)
                                                             (do (swap! state dissoc :error?)
                                                                 (swap! state assoc :value value)
                                                                 (reset-final-confirmation!))))
                                   :placeholder          (if (= action-type :clear-selected)
                                                           (loc/tr :enter-value-to-be-cleared)
                                                           (loc/tr :enter-new-value))
                                   :value                value
                                   :drag-options         {:draggable?      false
                                                          :hide-drag-area? true}
                                   :validate!            (fn [attribute value]
                                                           (let [field-schema (registry/get-field-schema tenant-schema
                                                                                                         attribute)
                                                                 field-constraints (:field/constraints field-schema)
                                                                 errors (constraints/validate field-constraints
                                                                                              "test-id"
                                                                                              value
                                                                                              (constantly nil)
                                                                                              []
                                                                                              loc/tr
                                                                                              logging/warn)]
                                                             (if errors
                                                               (swap! state assoc :validation-error errors)
                                                               (swap! state dissoc :validation-error))
                                                             errors))})

                    editor-props (merge editor-props
                                        (when (= selected-field :oppo/target-asset)
                                          {:editor/type        :editor.type/select
                                           :field/entity-types pipeline-target-asset-types}))]
                [:div
                 [field-editor/view
                  editor-props]]))]

           [:div
            (let [confirmation-visible? (condp = selected-field

                                          :oppo/status
                                          (let [new-status (:status @state)
                                                pass-reason (:pass-reason @state)
                                                on-hold-months (:months @state)
                                                on-hold-until (:until @state)]
                                            (or (= new-status :oppo.status/ok)
                                                (and (= new-status :oppo.status/passed)
                                                     pass-reason)
                                                (and (= new-status :oppo.status/on-hold)
                                                     on-hold-months
                                                     (or (not= on-hold-months 0)
                                                         on-hold-until))))

                                          (let [action-type (:action-type @state)
                                                value (:value @state)
                                                empty-value? (or (= value nil)
                                                                 (cu/empty-value? value))]
                                            (and (not (:validation-error @state))
                                                 (or (= action-type :clear-all)
                                                     (and (= field-data-type :data.type/boolean)
                                                          action-type)
                                                     (not empty-value?)))))]

              (if confirmation-visible?
                [:div
                 {:class "flex gap-3 items-baseline"}
                 [checkbox/view
                  {:value     (-> @state
                                  :final-confirmation?
                                  boolean)
                   :on-change #(swap! state update :final-confirmation? not)}]
                 [:span
                  (loc/tr :bulk-edit-confirmation [entity-label])]]
                [:div]))

            (when error?
              [:div
               {:class "p-3 flex gap-3 rounded bg-danger-light"}
               [:i.fas.fa-info-circle
                {:class "text-danger-light-text"}]
               [:div "text-danger-light-text"
                [:p (loc/tr :bulk-edit-error-msg)]]])]]]

         [modal/footer-children-left-and-right
          [[button/secondary-hollow
            {:on-click #(close-modal!)}
            (loc/tr :cancel)]]
          [[button/primary-solid
            {:on-click  #(execute-bulk-edit! true)
             :disabled? execute-btn-disabled?}
            (loc/tr :save-and-continue)]

           [button/primary-solid
            {:on-click  #(execute-bulk-edit! false)
             :disabled? execute-btn-disabled?}
            (loc/tr :save-and-close)]]]]))))
